import { useEffect, useMemo, useState } from "react";
import authAxios from "../../../api/authAxios";

/**
 * ENTERPRISE-GRADE USER MANAGER
 * - Fetch users
 * - Create / edit users
 * - Delete users
 * - Sync site memberships
 * - SAFE against undefined orgs
 */
export default function useUserManager({
  currentUser,
  organizations = [],
  activeOrgId,
}) {
  /* ---------------------------------------------------
   * Guards
   * --------------------------------------------------- */
  const safeOrganizations = Array.isArray(organizations) ? organizations : [];
  const globalRole = (currentUser?.global_role || "").toLowerCase();
  const isPlatformAdmin = globalRole === "platform_admin";

  const activeOrg = useMemo(() => {
    if (!activeOrgId) return null;
    return safeOrganizations.find(
      (o) => Number(o.id) === Number(activeOrgId)
    );
  }, [safeOrganizations, activeOrgId]);

  /* ---------------------------------------------------
   * State
   * --------------------------------------------------- */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  /* ---------------------------------------------------
   * FETCH USERS
   * --------------------------------------------------- */
  const fetchUsers = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // PLATFORM ADMIN → global list
      if (isPlatformAdmin) {
        const res = await authAxios.get("/users/list/");
        setUsers(Array.isArray(res.data) ? res.data : []);
        return;
      }

      // ORG ADMIN / USER → org scoped
      if (activeOrg?.members) {
        const normalized = activeOrg.members.map((m) => ({
          id: m.user.id,
          name: m.user.name,
          email: m.user.email,
          memberships: [
            {
              organization: {
                id: activeOrg.id,
                name: activeOrg.name,
                slug: activeOrg.slug,
              },
              role: m.role,
            },
          ],
        }));

        setUsers(normalized);
      } else {
        setUsers([]);
      }
    } catch (err) {
      console.error("❌ fetchUsers failed:", err?.response?.data || err.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------
   * CREATE / EDIT USER
   * --------------------------------------------------- */
  const submitUser = async (payload) => {
    try {
      let userId = payload.id;

      // 1️⃣ CREATE
      if (!userId) {
        const body = {
          name: payload.name,
          email: payload.email,
          password: payload.password,
          organization: payload.organization,
          org_role: payload.org_role,
        };

        if (isPlatformAdmin && payload.global_role) {
          body.global_role = payload.global_role;
        }

        const res = await authAxios.post("/users/", body);
        userId = res.data.id;
      }
      // 2️⃣ UPDATE
      else {
        const body = {
          name: payload.name,
          email: payload.email,
          organization: payload.organization,
          org_role: payload.org_role,
        };

        if (payload.password) body.password = payload.password;
        if (isPlatformAdmin && payload.global_role) {
          body.global_role = payload.global_role;
        }

        await authAxios.patch(`/users/${userId}/`, body);
      }

      // 3️⃣ SITE MEMBERSHIPS
      if (Array.isArray(payload.sites)) {
        await authAxios.post(`/users/${userId}/sites/`, {
          sites: payload.sites,
          default_site_id: payload.default_site_id ?? null,
        });
      }

      // 4️⃣ CLEANUP
      setShowUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("❌ submitUser failed:", err?.response?.data || err.message);
      throw err;
    }
  };

  /* ---------------------------------------------------
   * DELETE USER
   * --------------------------------------------------- */
  const deleteUser = async (userId) => {
    if (!window.confirm("Remove this user?")) return;

    try {
      // PLATFORM ADMIN → hard delete
      if (isPlatformAdmin) {
        await authAxios.delete(`/users/${userId}/`);
      }
      // ORG ADMIN → remove membership only
      else if (activeOrg?.id) {
        const res = await authAxios.get(
          `/memberships/?organization=${activeOrg.id}&user=${userId}`
        );

        const membership = res.data?.[0];
        if (!membership) {
          alert("User is not part of this organization.");
          return;
        }

        await authAxios.delete(`/memberships/${membership.id}/`);
      }

      fetchUsers();
    } catch (err) {
      console.error("❌ deleteUser failed:", err?.response?.data || err.message);
      alert("You do not have permission to remove this user.");
    }
  };

  /* ---------------------------------------------------
   * EFFECTS
   * --------------------------------------------------- */
  useEffect(() => {
    fetchUsers();
  }, [currentUser, activeOrgId, safeOrganizations.length]);

  /* ---------------------------------------------------
   * PUBLIC API
   * --------------------------------------------------- */
  return {
    users,
    loading,
    isPlatformAdmin,
    activeOrg,
    showUserModal,
    setShowUserModal,
    editingUser,
    setEditingUser,
    fetchUsers,
    submitUser,
    deleteUser,
  };
}

